<template>
  <div class="container">
    <h5 class="mb-4 mt-2 title-text">
      {{ t("PAYJOY_CARD_VIEWS.SELECT_CARD_TYPE_TITLE") }}
    </h5>

    <LimitIncreaseButton
      class="limit-increase-button"
      v-if="showLimitIncreasePromotionBanner"
      :expiry-date="newLimitOfferExpiry"
      :limit-increase-amount="newCreditLimit"
      @cta-clicked="clickNewLimitAmountBanner"
    />

    <div v-for="(card, index) in cardTypes" :key="index">
      <CardBox
        v-if="card.visible"
        :imageSrc="card.image"
        :title="card.title"
        :subtitle="card.subtitle"
        @click="cardSelected(card.type)"
      />
    </div>
    <div
      id="cl-inline-message-container-select-card-type"
      class="mx-auto d-flex flex-column gap-2"
    ></div>
  </div>
</template>

<script setup lang="ts">
import useAsset from "@/apps/credit-line/helpers/useAsset";
import CardBox from "./CardBox.vue";
import { onMounted, onBeforeMount, ref } from "vue";
import { CardType } from "@/apps/credit-line/types/cardType";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import LimitIncreaseButton from "@/apps/credit-line/components/LimitIncreaseButton.vue";
import { useNewLimitIncreaseOffer } from "@/apps/credit-line/hooks/useNewLimitIncreaseOffer";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { LogType } from "@/types/dataDogLogType";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { cioanalytics } from "@/apps/credit-line/services/customerio.service";

declare const Android: AndroidInterface;

const { t } = useI18n();
const deviceTag = ref<string>(AuthenticationHelper.getDeviceTag());

const props = defineProps({
  shouldShowVirtualCard: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits<{
  (e: "selectCardType", value: string): void;
  (e: "limit-increase-banner-click"): void;
}>();

const {
  showLimitIncreasePromotionBanner,
  newCreditLimit,
  newLimitOfferExpiry,
} = useNewLimitIncreaseOffer();

const cardSelected = (type: string) => {
  emit("selectCardType", type as CardType);
};

const showNav = () => {
  if (Android && typeof Android?.showNav === "function") {
    Android.showNav();
  }
};

const cardTypes = [
  {
    image: useAsset("img/credit-line/credit-card/physical-card.svg"),
    title: t("PAYJOY_CARD_VIEWS.PHYSICAL_CARD_TITLE"),
    subtitle: t("PAYJOY_CARD_VIEWS.PHYSICAL_CARD_SUBTITLE"),
    visible: true,
    type: "PHYSICAL",
  },
  {
    image: useAsset("img/credit-line/credit-card/virtual-card.svg"),
    title: t("PAYJOY_CARD_VIEWS.VIRTUAL_CARD_TITLE"),
    subtitle: t("PAYJOY_CARD_VIEWS.VIRTUAL_CARD_SUBTITLE"),
    visible: props.shouldShowVirtualCard,
    type: "VIRTUAL",
  },
];

const clickNewLimitAmountBanner = () => emit("limit-increase-banner-click");

onMounted(() => {
  showNav();
});

onBeforeMount(() => {
  DatadogLogger.Log(
    "CustomerIo identify called on SelectCardType screen",
    {
      space: "SelectCardType",
      deviceTag: deviceTag.value,
    },
    LogType.INFO
  );

  cioanalytics?.identify(deviceTag.value);
  cioanalytics?.track("creditline SelectCardType screen ");
});
</script>

<style scoped>
.title-text {
  font-weight: 600;
  font-size: 20px;
  line-height: 29px;
  font-family: "Inter", sans-serif;
}

.limit-increase-button {
  margin-bottom: 12px;
}
</style>
